import { useEffect, useState, useRef, useMemo } from 'react';
import { X, User, DollarSign, Calendar, Send } from 'react-feather';
import { useClaims } from '../../Context/ClaimsContext';
import { useAuth } from '../../Context/AuthContext';
import StatusBadge from '../Tables/StatusBadge';

interface ClaimDetailPanelProps {
  isOpen: boolean;
  onClose: () => void;
  claim: any;
}

const ClaimDetailPanel = ({ isOpen, onClose, claim }: ClaimDetailPanelProps) => {
  const { episodeDetail, episodeLoading, getEpisodeDetail, storeEpisodeNote } = useClaims();
  const { currentUser } = useAuth();

  const [isClosing, setIsClosing] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const [noteContent, setNoteContent] = useState('');
  const [highlightedClaimId, setHighlightedClaimId] = useState<string | null>(null);

  const panelRef = useRef<HTMLDivElement>(null);

  // Build episode params from the selected claim row
  const getEpisodeParams = () => {
    if (!claim) return null;
    return {
      name: claim.name_raw || claim.name,
      policy_id: claim.policy_id,
      facility: claim.facility_raw || claim.facility,
      start_date: claim.start_date,
      end_date: claim.end_date,
    };
  };

  // Fetch episode detail when claim changes
  useEffect(() => {
    if (claim && isOpen) {
      const params = getEpisodeParams();
      if (params) getEpisodeDetail(params);
    }
  }, [claim, isOpen]);

  // Escape key handler
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') handleClose();
    };
    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
    }
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen]);

  // Opening animation
  useEffect(() => {
    if (isOpen) {
      const timer = setTimeout(() => setIsVisible(true), 10);
      return () => clearTimeout(timer);
    } else {
      setIsVisible(false);
    }
  }, [isOpen]);

  const handleClose = () => {
    setIsClosing(true);
    setTimeout(() => {
      setIsClosing(false);
      onClose();
    }, 300);
  };

  const handleSubmitNote = () => {
    if (!noteContent.trim() || !episodeDetail?.claims?.length) return;

    const latestClaimId = episodeDetail.claims[episodeDetail.claims.length - 1]?.claim_id;
    const today = new Date();
    const dateStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;

    const noteData = {
      claim_id: latestClaimId,
      date: dateStr,
      notes: noteContent,
      coordinator: currentUser?.userId || '',
    };

    const params = getEpisodeParams();
    if (params) {
      storeEpisodeNote(noteData, params);
      setNoteContent('');
    }
  };

  const formatCurrency = (val: number | string) => {
    const num = typeof val === 'string' ? parseFloat(val) : val;
    if (isNaN(num)) return '$0.00';
    return `$${num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const formatDateShort = (dateStr: string): string => {
    if (!dateStr) return '-';
    const match = dateStr.match(/^(\d{2})-(\d{2})-(\d{4})$/);
    if (match) {
      const [, mm, dd, yyyy] = match;
      return `${mm}/${dd}/${yyyy}`;
    }
    const isoMatch = dateStr.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (isoMatch) {
      const [, yyyy, mm, dd] = isoMatch;
      return `${mm}/${dd}/${yyyy}`;
    }
    return dateStr;
  };

  const formatFacilityDisplay = (facility: string): string => {
    if (!facility) return '-';
    const f = facility.toLowerCase();
    if (f.includes('beachside')) return 'Beachside';
    if (f.includes('axis')) return 'Axis';
    if (f.includes('affinity')) return 'Affinity';
    return facility;
  };

  // Compute episode summary from detail
  const episodeSummary = (() => {
    if (!episodeDetail?.claims?.length) return null;
    const claims = episodeDetail.claims;
    const totalCharged = claims.reduce((sum: number, c: any) => sum + (c.charged_total || 0), 0);
    const totalPaid = claims.reduce((sum: number, c: any) => sum + (c.paid_total || 0), 0);
    const totalBalance = claims.reduce((sum: number, c: any) => sum + (c.balance_total || 0), 0);
    return {
      totalDays: claims.length,
      totalCharged,
      totalPaid,
      totalBalance,
      startDate: claims[0]?.end_date || '',
      endDate: claims[claims.length - 1]?.end_date || '',
    };
  })();

  const episodeStatus = claim?.status || '';

  // Merge user notes + follow-up notes into one list, sorted by date desc
  const allNotes = useMemo(() => {
    const merged: { date: string; text: string; coordinator?: string; source: 'user' | 'collab'; claim_ids?: string[] }[] = [];

    if (episodeDetail?.notes) {
      for (const n of episodeDetail.notes) {
        merged.push({ date: n.date, text: n.notes, coordinator: n.coordinator, source: 'user' });
      }
    }
    if (episodeDetail?.follow_up_notes) {
      // Group follow-up notes by text so shared notes show all claim IDs
      const fuGroups = new Map<string, { date: string; claim_ids: string[] }>();
      for (const n of episodeDetail.follow_up_notes) {
        const existing = fuGroups.get(n.fu_note);
        if (existing) {
          existing.claim_ids.push(n.claim_id);
        } else {
          fuGroups.set(n.fu_note, { date: n.service_date, claim_ids: [n.claim_id] });
        }
      }
      fuGroups.forEach((group, text) => {
        merged.push({ date: group.date, text, claim_ids: group.claim_ids, source: 'collab' });
      });
    }

    // Sort newest first by raw date string (both are MM-DD-YYYY from backend)
    merged.sort((a, b) => {
      const toSortable = (d: string) => {
        const m = d?.match(/^(\d{2})-(\d{2})-(\d{4})$/);
        return m ? `${m[3]}${m[1]}${m[2]}` : d || '';
      };
      return toSortable(b.date).localeCompare(toSortable(a.date));
    });

    return merged;
  }, [episodeDetail]);

  if (!isOpen && !isClosing) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className={`fixed inset-0 bg-black/60 z-40 transition-opacity duration-300 ${
          isVisible && !isClosing ? 'opacity-100' : 'opacity-0'
        }`}
        onClick={handleClose}
      />
      {/* Panel */}
      <div
        ref={panelRef}
        className={`fixed inset-0 md:inset-y-0 md:left-auto md:right-0 h-full w-full md:max-w-4xl bg-dark-bg md:border-l border-dark-border shadow-2xl z-50 overflow-hidden flex flex-col transition-transform duration-300 ease-out ${
          isVisible && !isClosing ? 'translate-x-0' : 'translate-x-full'
        }`}
        style={{ paddingTop: 'env(safe-area-inset-top)', paddingBottom: 'env(safe-area-inset-bottom)' }}
      >
        {/* Header */}
        <div className="flex-shrink-0 px-4 md:px-6 py-4 md:py-5 border-b border-dark-border bg-dark-card">
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-center gap-3 md:gap-4 min-w-0">
              <div className="w-12 h-12 md:w-14 md:h-14 rounded-2xl bg-gradient-to-br from-accent-teal to-accent-purple flex items-center justify-center shadow-lg flex-shrink-0">
                <User className="w-6 h-6 md:w-7 md:h-7 text-white" />
              </div>
              <div className="min-w-0">
                <h2 className="text-lg md:text-xl font-bold text-white truncate">{claim?.name || '-'}</h2>
                <p className="text-sm text-gray-400 truncate">Policy: {claim?.policy_id || '-'}</p>
              </div>
            </div>
            <button
              onClick={handleClose}
              className="p-2 hover:bg-dark-card-hover rounded-xl transition-colors flex-shrink-0"
            >
              <X className="w-5 h-5 text-gray-400" />
            </button>
          </div>
        </div>

        {/* Scrollable Content */}
        <div className="flex-1 overflow-y-auto">
          {episodeLoading ? (
            <div className="flex items-center justify-center py-20">
              <div className="w-8 h-8 border-2 border-accent-teal border-t-transparent rounded-full animate-spin" />
            </div>
          ) : (
            <div className="px-4 md:px-6 py-4 space-y-5">
              {/* Info Badges - 2x2 grid */}
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-dark-card border border-dark-border rounded-xl p-3">
                  <span className="text-xs text-gray-500 block mb-1">Insurance</span>
                  <span className="text-sm text-white font-medium truncate block">{claim?.insurance || '-'}</span>
                </div>
                <div className="bg-dark-card border border-dark-border rounded-xl p-3">
                  <span className="text-xs text-gray-500 block mb-1">Facility</span>
                  <StatusBadge status={formatFacilityDisplay(claim?.facility_raw || claim?.facility || '')} type="facility" />
                </div>
                <div className="bg-dark-card border border-dark-border rounded-xl p-3">
                  <span className="text-xs text-gray-500 block mb-1">Network</span>
                  <StatusBadge status={claim?.network || '-'} type="network" />
                </div>
                <div className="bg-dark-card border border-dark-border rounded-xl p-3">
                  <span className="text-xs text-gray-500 block mb-1">Status</span>
                  <StatusBadge status={episodeStatus} type="claim_status" />
                </div>
              </div>

              {/* Episode Summary */}
              {episodeSummary && (
                <div className="bg-dark-card border border-dark-border rounded-xl p-4">
                  <h3 className="text-sm font-semibold text-white mb-3 flex items-center gap-2">
                    <Calendar className="w-4 h-4 text-accent-teal" />
                    Episode Summary
                  </h3>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    <div>
                      <span className="text-xs text-gray-500 block">Date Range</span>
                      <span className="text-sm text-white">{formatDateShort(episodeSummary.startDate)} - {formatDateShort(episodeSummary.endDate)}</span>
                    </div>
                    <div>
                      <span className="text-xs text-gray-500 block">Total Days</span>
                      <span className="text-sm text-white font-semibold">{episodeSummary.totalDays}</span>
                    </div>
                    <div>
                      <span className="text-xs text-gray-500 block">Charged</span>
                      <span className="text-sm text-white">{formatCurrency(episodeSummary.totalCharged)}</span>
                    </div>
                    <div>
                      <span className="text-xs text-gray-500 block">Paid</span>
                      <span className="text-sm text-green-400 font-semibold">{formatCurrency(episodeSummary.totalPaid)}</span>
                    </div>
                  </div>
                  <div className="mt-3 flex items-center gap-4">
                    <div>
                      <span className="text-xs text-gray-500 block">Outstanding Balance</span>
                      <span className={`text-sm font-semibold ${episodeSummary.totalBalance > 0 ? 'text-orange-400' : 'text-green-400'}`}>
                        {formatCurrency(episodeSummary.totalBalance)}
                      </span>
                    </div>
                    {episodeSummary.totalCharged > 0 && (
                      <div>
                        <span className="text-xs text-gray-500 block">Reimbursement</span>
                        <span className={`text-sm font-semibold ${
                          (episodeSummary.totalPaid / episodeSummary.totalCharged) >= 0.5 ? 'text-green-400' :
                          (episodeSummary.totalPaid / episodeSummary.totalCharged) > 0 ? 'text-yellow-400' : 'text-red-400'
                        }`}>
                          {((episodeSummary.totalPaid / episodeSummary.totalCharged) * 100).toFixed(1)}%
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Day-by-Day Table */}
              {episodeDetail?.claims && episodeDetail.claims.length > 0 && (
                <div className="bg-dark-card border border-dark-border rounded-xl overflow-hidden">
                  <div className="px-4 py-3 border-b border-dark-border">
                    <h3 className="text-sm font-semibold text-white flex items-center gap-2">
                      <DollarSign className="w-4 h-4 text-accent-teal" />
                      Day-by-Day Breakdown
                    </h3>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b border-dark-border">
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-400 uppercase">Date</th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-400 uppercase">Claim ID</th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-400 uppercase">Control #</th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-400 uppercase">CPT</th>
                          <th className="px-4 py-2 text-right text-xs font-medium text-gray-400 uppercase">Charged</th>
                          <th className="px-4 py-2 text-right text-xs font-medium text-gray-400 uppercase">Paid</th>
                          <th className="px-4 py-2 text-right text-xs font-medium text-gray-400 uppercase">Outstanding</th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-400 uppercase">Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {episodeDetail.claims.map((c: any, idx: number) => (
                          <tr key={idx} className={`border-b border-dark-border/50 transition-all duration-500 ${highlightedClaimId === c.claim_id ? 'bg-accent-teal/15 animate-pulse' : 'hover:bg-dark-card-hover'}`}>
                            <td className="px-4 py-2.5 text-sm text-gray-300">{formatDateShort(c.end_date)}</td>
                            <td className="px-4 py-2.5 text-sm text-gray-400">{c.claim_id || '-'}</td>
                            <td className="px-4 py-2.5 text-sm text-gray-400">{c.primary_claim_control || '-'}</td>
                            <td className="px-4 py-2.5 text-sm text-gray-400">{c.charge_code || '-'}</td>
                            <td className="px-4 py-2.5 text-sm text-gray-300 text-right">{formatCurrency(c.charged_total)}</td>
                            <td className="px-4 py-2.5 text-sm text-green-400 text-right">{formatCurrency(c.paid_total)}</td>
                            <td className="px-4 py-2.5 text-sm text-orange-400 text-right">{formatCurrency(c.balance_total)}</td>
                            <td className="px-4 py-2.5">
                              <StatusBadge status={c.status || ''} type="claim_status" />
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Notes Section */}
              <div className="bg-dark-card border border-dark-border rounded-xl overflow-hidden">
                <div className="px-4 py-3 border-b border-dark-border">
                  <h3 className="text-sm font-semibold text-white">
                    Notes ({allNotes.length})
                  </h3>
                </div>

                <div className="max-h-[350px] overflow-y-auto">
                  {allNotes.length > 0 ? (
                    allNotes.map((note, idx) => (
                      <div key={idx} className="px-4 py-3 border-b border-dark-border/50 last:border-0">
                        <div className="flex items-center justify-between mb-1">
                          <div className="flex items-center gap-2">
                            <span className="text-xs text-gray-500">{formatDateShort(note.date)}</span>
                            {note.source === 'collab' && (
                              <>
                                <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-400 border border-amber-500/20">CollabMD</span>
                                {note.claim_ids && note.claim_ids.map((cid, i) => (
                                  <span
                                    key={cid}
                                    className="text-[10px] text-gray-500 hover:text-accent-teal cursor-pointer transition-colors"
                                    onClick={() => {
                                      setHighlightedClaimId(null);
                                      setTimeout(() => setHighlightedClaimId(cid), 10);
                                    }}
                                  >
                                    #{cid}{i < note.claim_ids!.length - 1 ? ',' : ''}
                                  </span>
                                ))}
                              </>
                            )}
                          </div>
                          {note.coordinator && (
                            <span className="text-xs text-gray-500 truncate ml-2">{note.coordinator}</span>
                          )}
                        </div>
                        <p className="text-sm text-gray-300 whitespace-pre-wrap">{note.text}</p>
                      </div>
                    ))
                  ) : (
                    <div className="px-4 py-6 text-center text-sm text-gray-500">
                      No notes yet
                    </div>
                  )}
                </div>

                {/* Add Note Form */}
                <div className="px-4 py-3 border-t border-dark-border bg-dark-input/30">
                  <div className="flex items-center gap-2">
                    <input
                      type="text"
                      placeholder="Add a note..."
                      value={noteContent}
                      onChange={(e) => setNoteContent(e.target.value)}
                      onKeyDown={(e) => { if (e.key === 'Enter') handleSubmitNote(); }}
                      className="flex-1 px-3 py-2 bg-dark-input border border-dark-border rounded-lg text-white text-sm placeholder-gray-500 focus:outline-none focus:border-accent-teal"
                    />
                    <button
                      onClick={handleSubmitNote}
                      disabled={!noteContent.trim()}
                      className="p-2 bg-accent-teal text-black rounded-lg hover:opacity-90 transition-opacity disabled:opacity-30 disabled:cursor-not-allowed"
                    >
                      <Send className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default ClaimDetailPanel;
